import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import { aggregateBookingsByMonth, fetchBookingsChartData } from './fetchBookingsChartData';

const CHART_GOLD = '#C9A84C';
const CHART_GOLD_LIGHT = '#f5e6b8';
const CHART_NAVY = '#0D1B2A';
const CHART_GRID = 'rgba(201, 168, 76, 0.15)';
const CHART_AXIS = '#8896a8';
const CHART_HEIGHT = 280;

function formatRevenue(value) {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${Number(value || 0).toLocaleString('en-IN')}`;
}

function useChartWidth() {
  const [width, setWidth] = useState(320);
  const ref = React.useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect?.width || 0);
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width: Math.max(width, 280) };
}

function PremiumTooltip({ active, payload, label, valueLabel, formatValue }) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div
      className="rounded-xl border border-de-gold/40 px-4 py-3 shadow-2xl"
      style={{ background: 'linear-gradient(145deg, #0f2040, #0D1B2A)' }}
    >
      <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-de-gold/80">{label}</p>
      <p className="m-0 mt-1 text-lg font-bold text-de-gold">
        {formatValue ? formatValue(val) : val}
      </p>
      <p className="m-0 mt-0.5 text-[10px] text-de-muted">{valueLabel}</p>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex h-[280px] flex-col justify-center gap-3 px-4">
      <div className="de-skeleton h-3 w-1/3" />
      <div className="de-skeleton h-[200px] w-full rounded-xl" />
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="de-ultra-card">
      <div className="border-b border-de-gold/15 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="de-stat-ring h-10 w-10">
            <Icon className="h-5 w-5 text-de-gold" />
          </div>
          <div>
            <p className="m-0 text-[10px] font-bold uppercase tracking-[0.2em] text-de-gold/70">Analytics</p>
            <h3 className="m-0 text-sm font-bold text-white">{title}</h3>
          </div>
        </div>
      </div>
      <div className="p-4 pt-2">{children}</div>
    </div>
  );
}

export default function AdminOverviewCharts() {
  const [series, setSeries] = useState([]);
  const [chartError, setChartError] = useState('');
  const [chartLoading, setChartLoading] = useState(true);
  const { ref: lineWrapRef, width: lineWidth } = useChartWidth();
  const { ref: barWrapRef, width: barWidth } = useChartWidth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChartLoading(true);
      const { rows, error } = await fetchBookingsChartData();
      if (cancelled) return;
      if (error) {
        setChartError(error);
        setSeries([]);
      } else {
        setChartError('');
        setSeries(aggregateBookingsByMonth(rows));
      }
      setChartLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const empty = !chartLoading && series.length === 0;
  const chartMargin = useMemo(() => ({ top: 12, right: 12, left: 4, bottom: 4 }), []);
  const maxRevenue = useMemo(() => Math.max(...series.map((s) => s.revenue), 1), [series]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="m-0 text-[10px] font-bold uppercase tracking-[0.35em] text-de-gold/70">6-month intelligence</p>
        <span className="text-[10px] text-de-muted">Advance paid · Live data</span>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <ChartCard title="Monthly Revenue" icon={LineChartIcon}>
          {chartLoading ? (
            <ChartSkeleton />
          ) : chartError ? (
            <p className="py-20 text-center text-sm text-de-accent">{chartError}</p>
          ) : empty ? (
            <p className="py-20 text-center text-sm text-de-muted">No booking data</p>
          ) : (
            <div ref={lineWrapRef} className="w-full min-w-0" style={{ height: CHART_HEIGHT }}>
              <ComposedChart width={lineWidth} height={CHART_HEIGHT} data={series} margin={chartMargin}>
                <defs>
                  <linearGradient id="revenueGoldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_GOLD} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={CHART_GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: CHART_GRID }} tickLine={false} />
                <YAxis
                  tick={{ fill: CHART_AXIS, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatRevenue}
                  domain={[0, maxRevenue * 1.1]}
                />
                <Tooltip content={<PremiumTooltip valueLabel="Advance collected" formatValue={formatRevenue} />} />
                <Area type="monotone" dataKey="revenue" fill="url(#revenueGoldFill)" stroke="none" isAnimationActive={false} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={CHART_GOLD}
                  strokeWidth={3}
                  dot={{ fill: CHART_NAVY, stroke: CHART_GOLD, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, fill: CHART_GOLD, stroke: CHART_GOLD_LIGHT, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Bookings Volume" icon={BarChart3}>
          {chartLoading ? (
            <ChartSkeleton />
          ) : chartError ? (
            <p className="py-20 text-center text-sm text-de-accent">{chartError}</p>
          ) : empty ? (
            <p className="py-20 text-center text-sm text-de-muted">No booking data</p>
          ) : (
            <div ref={barWrapRef} className="w-full min-w-0" style={{ height: CHART_HEIGHT }}>
              <BarChart width={barWidth} height={CHART_HEIGHT} data={series} margin={chartMargin} barCategoryGap="20%">
                <defs>
                  <linearGradient id="barGoldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_GOLD_LIGHT} />
                    <stop offset="100%" stopColor={CHART_GOLD} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: CHART_GRID }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<PremiumTooltip valueLabel="Total bookings" />} />
                <Bar dataKey="bookings" fill="url(#barGoldGrad)" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false} />
              </BarChart>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
